import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, UserPlus, Receipt, LogOut, Menu, X,
  Stethoscope, Settings, FlaskConical, CalendarDays, ActivitySquare,
  Pill, Ticket, DatabaseBackup, ChevronLeft, ChevronRight,
  ShieldCheck, Activity, Wallet,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Logo3D from './Logo3D';
import LiveClock from './LiveClock';
import ThemeToggle from './ThemeToggle';
import { CLINIC_NAME, CLINIC_SUBTITLE } from '../utils/clinicConfig';
import { getNavItems } from '../pages/NavSettingsPage';
import { useSocketEvent } from '../context/SocketContext';
import { playNextPatientChime, playReceptionBell } from '../utils/clinicSound';

export default function AppLayout({ children, title, subtitle }) {
  // ── Font size zoom (persists across sessions) ────────────────────────────
  const [zoomLevel, setZoomLevel] = React.useState(() => Number(localStorage.getItem('app_zoom') || 100));

  React.useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;
    if (zoomLevel === 100) {
      root.style.removeProperty('zoom');
    } else {
      // Apply zoom to root; sidebar uses min-height so it stretches with content
      root.style.zoom = (zoomLevel / 100).toFixed(2);
    }
    localStorage.setItem('app_zoom', zoomLevel);
  }, [zoomLevel]);

  const zoomIn    = () => setZoomLevel(s => Math.min(s + 10, 150));
  const zoomOut   = () => setZoomLevel(s => Math.max(s - 10, 70));
  const zoomReset = () => setZoomLevel(100);
  const { user, logout } = useAuth();

  // ── Global alert + mute state ────────────────────────────────────────────────
  const [clinicAlert, setClinicAlert] = React.useState(null); // { type, title, body }

  function showAlert(type, title, body, picture) {
    setClinicAlert({ type, title, body, picture, id: Date.now() });
  }

  // ── Global sound notifications ──────────────────────────────────────────────
  useSocketEvent('reception:bell', (payload) => {
    if (user?.role === 'receptionist') {
      playReceptionBell(1.0);
      const docName = payload?.doctor ? `Dr. ${payload.doctor.replace(/^dr\.?\s*/i,'').trim()}` : 'A doctor';
      const roomTxt = payload?.room ? ` — ${payload.room}` : '';
      showAlert('bell', '🔔 ' + docName + ' is Calling!', docName + roomTxt + ' is calling reception — please attend immediately', payload?.profilePicture);
    }
  });

  useSocketEvent('queue:now-serving', (payload) => {
    const ns = payload?.nowServing || null;
    if (!ns) return;
    if (user?.role === 'receptionist') {
      playNextPatientChime(1.0);
      const calledByName = ns.calledByName || ns.calledBy;
      const calledBy = calledByName ? `Dr. ${calledByName.replace(/^dr\.?\s*/i,'').trim()}` : '';
      const roomTxt  = ns.calledByRoom ? ` (${ns.calledByRoom})` : '';
      const nextBody = `Token #${ns.tokenNumber}${ns.patientName ? ' — ' + ns.patientName : ''}` + (calledBy ? `\nCalled by: ${calledBy}${roomTxt}` : '');
      showAlert('next', '📢 Next Patient!', nextBody, ns.calledByPicture);
    }
  });

  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sidebar_collapsed') === 'true'
  );

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', collapsed ? 'true' : 'false');
  }, [collapsed]);

  // Close mobile sidebar when clicking outside
  useEffect(() => {
    if (!mobileOpen) return;
    const handler = (e) => {
      if (!e.target.closest('.sidebar')) setMobileOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [mobileOpen]);

  function handleLogout() { logout(); navigate('/login'); }

  // ── Dynamic nav items (order + visibility from localStorage) ────────────────
  const ICON_MAP_LAYOUT = {
    LayoutDashboard, Users, UserPlus, Receipt, Ticket,
    FlaskConical, CalendarDays, ActivitySquare, Stethoscope,
    Pill, Settings, DatabaseBackup, ShieldCheck, Wallet,
  };

  const [navItems, setNavItems] = useState(() =>
    getNavItems(user?.role).filter(i => !i.hidden).map(i => ({
      ...i,
      end: i.key === 'dashboard' || i.key === 'home',
      icon: ICON_MAP_LAYOUT[i.icon] || Settings,
    }))
  );

  // Re-read nav when settings are saved
  useEffect(() => {
    function onStorage(e) {
      if (e.key === 'nav_order_doctor' || e.key === 'nav_order_receptionist') {
        setNavItems(
          getNavItems(user?.role).filter(i => !i.hidden).map(i => ({
            ...i,
            end: i.key === 'dashboard' || i.key === 'home',
            icon: ICON_MAP_LAYOUT[i.icon] || Settings,
          }))
        );
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [user?.role]); // eslint-disable-line

  return (
    <>
      {clinicAlert && ReactDOM.createPortal(
        <ClinicAlert type={clinicAlert.type} title={clinicAlert.title} body={clinicAlert.body} picture={clinicAlert.picture} key={clinicAlert.id} onDismiss={() => setClinicAlert(null)} />,
        document.body
      )}
      <div className="app-shell">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div onClick={() => setMobileOpen(false)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1400 }} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${mobileOpen ? 'mobile-open' : ''} ${collapsed ? 'collapsed' : ''}`}>

        {/* Brand */}
        <div className="sidebar-brand">
          <Logo3D icon={<Stethoscope size={22} color="white" />} size={44} radius={6} breathe />
          {!collapsed && (
            <div>
              <div className="sidebar-brand-text">{CLINIC_NAME}</div>
              <div className="sidebar-brand-sub">{CLINIC_SUBTITLE}</div>
            </div>
          )}
        </div>

        {/* Nav items — its own scrollable region, so adding more menu items
            (like Expenses) never pushes the footer/Minimise button out of
            view below the fold; the sidebar itself keeps its fixed height
            and the header/footer stay pinned. */}
        <div className="sidebar-nav-scroll">
          {!collapsed && <div className="nav-section-label">Menu</div>}
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''} ${collapsed ? 'nav-item-icon' : ''}`}
              title={collapsed ? item.label : undefined}
              onClick={() => setMobileOpen(false)}>
              <span className="nav-icon-chip"><item.icon size={15} /></span>
              {!collapsed && item.label}
            </NavLink>
          ))}

          {/* Menu Settings — admin only */}
          {user?.isAdmin && (
            <NavLink to="/doctor/nav-settings"
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''} ${collapsed ? 'nav-item-icon' : ''}`}
              title="Menu Settings"
              style={{ marginTop:8, borderTop:'1px solid var(--color-border)', paddingTop:12 }}>
              <span className="nav-icon-chip"><Settings size={15} /></span>
              {!collapsed && 'Menu Settings'}
            </NavLink>
          )}

          {/* System Health — admin only */}
          {user?.isAdmin && (
            <NavLink to="/doctor/system-health"
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''} ${collapsed ? 'nav-item-icon' : ''}`}
              title="System Health">
              <span className="nav-icon-chip"><Activity size={15} /></span>
              {!collapsed && 'System Health'}
            </NavLink>
          )}
        </div>

        {/* Footer */}
        <div className="sidebar-footer">
          {!collapsed && (
            <div className="user-chip">
              <div className="user-avatar">{(user?.displayName || user?.username || '?').charAt(0).toUpperCase()}</div>
              <div>
                <div className="user-info-name">{user?.displayName || user?.username}</div>
                <div className="user-info-role">{user?.role}</div>
              </div>
            </div>
          )}
          <button className={`nav-item ${collapsed ? 'nav-item-icon' : ''}`}
            style={{ marginTop:8, color:'var(--color-danger)' }}
            title={collapsed ? 'Log out' : undefined}
            onClick={handleLogout}>
            <LogOut size={17} />
            {!collapsed && 'Log out'}
          </button>
        </div>

        {/* Zoom controls in sidebar — accessible on mobile */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:4, padding:'8px 0', borderTop:'1px solid var(--color-border)', marginTop:8, flexShrink:0 }}>
          <button type="button" onClick={zoomOut} disabled={zoomLevel <= 70}
            style={{ background:'var(--color-surface-raised)', border:'1px solid var(--color-border)', borderRadius:6, cursor:'pointer', padding:'4px 10px', fontSize:13, fontWeight:800, color:'var(--color-ink-soft)' }}>A−</button>
          <span style={{ fontSize:11, color:'var(--color-ink-faint)', minWidth:36, textAlign:'center', cursor:'pointer' }} onClick={zoomReset}>{zoomLevel}%</span>
          <button type="button" onClick={zoomIn} disabled={zoomLevel >= 150}
            style={{ background:'var(--color-surface-raised)', border:'1px solid var(--color-border)', borderRadius:6, cursor:'pointer', padding:'4px 10px', fontSize:15, fontWeight:800, color:'var(--color-ink-soft)' }}>A+</button>
        </div>

        {/* Desktop collapse toggle — pinned to bottom of sidebar */}
        <button
          className="sidebar-collapse-btn"
          onClick={() => setCollapsed(c => !c)}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
          {!collapsed && <span style={{ fontSize:12 }}>Minimise</span>}
        </button>
      </aside>

      {/* Main */}
      <main className="main-content">
        <div className="topbar no-print">
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            {/* Mobile menu button — shown only on small screens via CSS */}
            <button className="btn btn-icon btn-outline mobile-menu-btn"
              onClick={() => setMobileOpen(o => !o)}>
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            <div>
              <div className="page-title topbar-title">{title}</div>
              {subtitle && <div className="page-subtitle">{subtitle}</div>}
            </div>
          </div>
          <div className="topbar-right">
            <LiveClock />
            {/* Zoom controls — visible on desktop, hidden on mobile (in sidebar instead) */}
            <div className="zoom-controls no-print" style={{ display:'flex', alignItems:'center', gap:2, border:'1px solid var(--color-border)', borderRadius:8, padding:'2px 4px' }} title="Page zoom">
              <button type="button" onClick={zoomOut} disabled={zoomLevel <= 70}
                style={{ background:'none', border:'none', cursor:'pointer', padding:'3px 8px', fontSize:13, fontWeight:800, color:'var(--color-ink-soft)', lineHeight:1 }}
                title="Zoom out">A−</button>
              <span className="zoom-pct" style={{ fontSize:11, color:'var(--color-ink-faint)', minWidth:32, textAlign:'center', userSelect:'none', cursor:'pointer' }}
                onClick={zoomReset} title="Reset zoom">{zoomLevel}%</span>
              <button type="button" onClick={zoomIn} disabled={zoomLevel >= 150}
                style={{ background:'none', border:'none', cursor:'pointer', padding:'3px 8px', fontSize:15, fontWeight:800, color:'var(--color-ink-soft)', lineHeight:1 }}
                title="Zoom in">A+</button>
            </div>
            <ThemeToggle />
          </div>
        </div>
        {children}
      </main>

      <style>{`
        @media (max-width: 860px) {
          #mobile-menu-btn { display: flex !important; }
        }
      `}</style>
      </div>
    </>
  );
}

// ── ClinicAlert — big center overlay notification for reception ───────────────
function ClinicAlert({ type, title, body, picture, onDismiss }) {
  const isBell = type === 'bell';
  const accent = isBell ? '#d97706' : 'var(--color-primary)';
  const bg     = isBell ? '#fffbeb' : '#eff6ff';
  const icon   = isBell ? '🔔' : '📢';



  return (
    <>
      {/* Dark backdrop */}
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 9990,
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(4px)',
        }}
      />
      {/* Alert box */}
      <div
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 9991,
          background: bg,
          border: `3px solid ${accent}`,
          borderRadius: 20,
          padding: '36px 48px',
          textAlign: 'center',
          boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
          minWidth: 320,
          maxWidth: '90vw',
          animation: 'clinicPop 0.25s ease',
        }}
      >
        {picture ? (
          <div style={{ position:'relative', width:96, height:96, margin:'0 auto 16px' }}>
            <div style={{ width:96, height:96, borderRadius:'50%', overflow:'hidden', border:`3px solid ${accent}`, boxShadow:`0 4px 16px ${accent}55` }}>
              <img src={picture} alt="Doctor" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
            </div>
            <div style={{ position:'absolute', bottom:-4, right:-4, fontSize:28, lineHeight:1, background:bg, borderRadius:'50%' }}>{icon}</div>
          </div>
        ) : (
          <div style={{ fontSize: 64, lineHeight: 1, marginBottom: 16 }}>{icon}</div>
        )}
        <div style={{ fontWeight: 900, fontSize: 26, color: accent, marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 16, color: '#374151', marginBottom: 28, lineHeight: 1.6, whiteSpace: 'pre-line' }}>{body}</div>
        <button
          onClick={onDismiss}
          style={{
            background: accent, color: '#fff',
            border: 'none', borderRadius: 10,
            padding: '10px 32px', fontSize: 15, fontWeight: 700,
            cursor: 'pointer', boxShadow: `0 4px 14px ${accent}55`,
          }}
        >
          OK / Dismiss
        </button>

      </div>
      <style>{`
        @keyframes clinicPop {
          from { transform: translate(-50%, -50%) scale(0.7); opacity: 0; }
          to   { transform: translate(-50%, -50%) scale(1);   opacity: 1; }
        }
      `}</style>
    </>
  );
}
